/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Frequently Asked Questions section with expandable items
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'How does text-to-image work?',
      answer:
        'Text-to-image technology takes the words you type and turns them into visual instructions. The AI model analyzes each part of your prompt—such as the subject, style, lighting, and mood—and then generates an image that fits those details. It uses patterns learned from millions of examples to build a new picture from scratch. The more specific your prompt is, the more accurate and detailed the final result becomes.',
    },
    {
      id: 'faq-2',
      question: 'How does image-to-image work?',
      answer:
        'Image-to-image generation starts with a photo or illustration you upload. The AI reads the shapes, colors, and structure of the original image, then transforms it based on your instructions. You can change the style, adjust the mood, or create a new version while keeping the core layout. This method is great for refining ideas, testing variations, or turning rough sketches into polished visuals.',
    },
    {
      id: 'faq-3',
      question: 'What is the best AI image generator?',
      answer: (
        <>
          The best AI image generator is one that offers high-quality output, fast
          processing, and tools for both beginners and advanced creators. FalcoCut ranks
          first because it combines text-to-image, image-to-image, upscaling, and style
          control in one simple platform. It also delivers clean, watermark-free results
          with an easy workflow.
          <br />
          <br />
          <strong>Best AI image generators (top picks):</strong>
          <br />
          1. FalcoCut — A fast, all-in-one AI image generator with clean output and
          beginner-friendly tools.
          <br />
          2. Midjourney — Known for artistic and stylized images with strong visual flair.
          <br />
          3. DALL·E — Offers detailed text-to-image generation with strong prompt
          understanding.
          <br />
          4. Stable Diffusion — A flexible open-source model for custom workflows and
          fine-tuning.
          <br />
          5. Adobe Firefly — Great for branded, safe-to-use images with strong editing
          support.
          <br />
          6. Bing Image Creator — A free option with quick generation and simple controls.
          <br />
          7. Leonardo AI — Popular for game assets, character art, and creative
          variations.
        </>
      ),
    },
    {
      id: 'faq-4',
      question: 'Do I need design skills to use FalcoCut AI image generator?',
      answer:
        'No design experience is required to use FalcoCut. All tools are built for beginners who want fast results without learning complex software. You only need to describe what you want, choose a style, and generate the image. Even advanced features like image-to-image or upscaling are easy to use, making the platform accessible for students, creators, and small business owners.',
    },
    {
      id: 'faq-5',
      question: 'How to use FalcoCut AI image generator?',
      answer: (
        <>
          <strong>Step 1:</strong> Enter a text prompt or upload an image you want to
          transform.
          <br />
          <strong>Step 2:</strong> Choose a model or style that matches the look you want.
          <br />
          <strong>Step 3:</strong> Adjust settings like aspect ratio, lighting, or
          resolution if needed.
          <br />
          <strong>Step 4:</strong> Generate the image, review the result, and download or
          refine it.
          <br />
          <br />
          This simple workflow lets anyone create high-quality visuals in just a few
          clicks.
        </>
      ),
    },
    {
      id: 'faq-6',
      question: 'Can AI create realistic or photorealistic images?',
      answer:
        'Yes, modern AI models can produce highly realistic or photorealistic images that look similar to real photography. These models understand textures, lighting, shadows, and facial details, allowing them to create lifelike results. With the right prompt and settings, AI can generate portraits, product shots, and scenes that appear professionally photographed. This makes it useful for marketing, e-commerce, and creative projects.',
    },
    {
      id: 'faq-7',
      question: 'Can FalcoCut upscale or enhance image resolution?',
      answer: (
        <>
          Yes. FalcoCut&apos;s AI Image Enhancer provides advanced upscaling and
          quality-boosting tools that make images sharper, clearer, and more detailed. It
          improves resolution for prints, product photos, and large displays while also
          refining edges, textures, and lighting for a more polished look. If you want to
          upgrade low-quality images without recreating them, the enhancer at{' '}
          <a
            href="https://falcocut.ai/ai-image-enhancer"
            className="text-[#7C3AED] hover:underline"
          >
            https://falcocut.ai/ai-image-enhancer
          </a>{' '}
          offers a fast and reliable way to do it.
        </>
      ),
    },
    {
      id: 'faq-8',
      question: 'How do I write better prompts?',
      answer:
        'Writing better prompts starts with being clear about the subject, style, and mood you want. Adding details like colors, lighting, camera angle, or environment helps the AI understand your vision. You can also mention specific art styles or keywords to guide the output. The more descriptive your prompt is, the more accurate and creative the final image will be. Experimenting with variations is a great way to discover what works best.',
    },
    {
      id: 'faq-9',
      question: 'Is the AI image generator free to use?',
      answer:
        'Yes. FalcoCut offers free access to core features so anyone can try text-to-image and image-to-image generation. Free users can create images with watermarks and explore different styles. Some advanced tools, such as higher-resolution models or extended usage limits, may require an upgrade. This flexible setup makes it easy to start creating without any upfront cost.',
    },
    {
      id: 'faq-10',
      question: 'Can I generate consistent characters or styles across images?',
      answer:
        'Yes, you can create consistent characters or styles by reusing the same prompts, descriptions, and reference images. Keeping details like clothing, colors, and facial features the same helps the AI maintain continuity. Image-to-image generation also supports consistency by using a previous output as the base. This is especially useful for storytelling, branding, or multi-image projects.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
